import { TechNode } from '../../types';

/**
 * Artificial Intelligence domain nodes
 * Tracking progress toward AGI and beyond
 */
export const AI_NODES: TechNode[] = [
  // === FOUNDATIONAL (Tier 0) ===
  {
    id: 'ai-neural-networks',
    name: 'Deep Neural Networks',
    description: 'Multi-layer neural networks enabling modern AI',
    domain: 'ai',
    status: 'achieved',
    yearAchieved: 2012,
    importance: 8,
    tier: 0,
    trl: 8,
    keyPlayers: ['Alex Krizhevsky', 'Ilya Sutskever', 'Geoffrey Hinton'],
    milestones: [
      {
        date: '2012-09-30',
        title: 'AlexNet wins ImageNet',
        description: 'Deep convnets won ILSVRC 2012; top-5 error 15.3%, ~11 pt lead over runner-up',
        significance: 'breakthrough',
        source: 'https://papers.nips.cc/paper/4824-imagenet-classification-with-deep-convolutional-neural-networks',
      },
    ],
  },
  {
    id: 'ai-transformers',
    name: 'Transformer Architecture',
    description: 'Attention-based architecture powering modern LLMs',
    domain: 'ai',
    status: 'achieved',
    yearAchieved: 2017,
    importance: 9,
    tier: 0,
    trl: 8,
    dependencies: ['ai-neural-networks'],
    keyPlayers: ['Google Brain', 'Vaswani et al.'],
    milestones: [
      {
        date: '2017-06-12',
        title: 'Attention Is All You Need',
        description: 'Paper introducing the Transformer architecture',
        significance: 'breakthrough',
        source: 'https://arxiv.org/abs/1706.03762',
      },
    ],
  },

  // === LANGUAGE MODELS (Tier 1) ===
  {
    id: 'ai-gpt3',
    name: 'GPT-3 Scale LLMs',
    description: '175B+ parameter language models with emergent capabilities',
    domain: 'ai',
    status: 'achieved',
    yearAchieved: 2020,
    importance: 8,
    tier: 1,
    trl: 8,
    dependencies: ['ai-transformers'],
    keyPlayers: ['OpenAI'],
    milestones: [
      {
        date: '2020-05-28',
        title: 'GPT-3 Paper',
        description: 'OpenAI publishes Language Models are Few-Shot Learners (175B params)',
        significance: 'breakthrough',
        source: 'https://arxiv.org/abs/2005.14165',
      },
      {
        date: '2020-06-11',
        title: 'GPT-3 API Beta',
        description: 'OpenAI launches API access for GPT-3',
        significance: 'major',
      },
    ],
  },
  {
    id: 'ai-chatgpt',
    name: 'Conversational AI',
    description: 'RLHF-tuned models for natural dialogue',
    domain: 'ai',
    status: 'achieved',
    yearAchieved: 2022,
    importance: 9,
    tier: 1,
    trl: 9,
    dependencies: ['ai-gpt3'],
    keyPlayers: ['OpenAI', 'Anthropic', 'Google'],
    milestones: [
      {
        date: '2022-11-30',
        title: 'ChatGPT Launch',
        description: 'Fastest-growing consumer app in history',
        significance: 'breakthrough',
        source: 'https://openai.com/blog/chatgpt',
      },
    ],
  },
  {
    id: 'ai-gpt4',
    name: 'GPT-4 Class Models',
    description: 'Multimodal models with professional-level reasoning',
    domain: 'ai',
    status: 'achieved',
    yearAchieved: 2023,
    importance: 9,
    tier: 1,
    trl: 9,
    dependencies: ['ai-chatgpt'],
    keyPlayers: ['OpenAI', 'Anthropic', 'Google'],
    progressMetric: 'MMLU Score',
    progressValue: '86.4%',
    progress: 86,
    milestones: [
      {
        date: '2023-03-14',
        title: 'GPT-4 Launch',
        description: 'OpenAI releases GPT-4 with image input and strong reasoning',
        significance: 'breakthrough',
        source: 'https://openai.com/index/gpt-4/',
      },
      {
        date: '2023-12-06',
        title: 'Gemini 1.0',
        description: 'Google launches native multimodal Gemini family',
        significance: 'major',
        source: 'https://blog.google/technology/ai/google-gemini-ai/',
      },
    ],
  },

  // === REASONING & AGENTS (Tier 2) ===
  {
    id: 'ai-reasoning',
    name: 'Chain-of-Thought Reasoning',
    description: 'Models that can break down complex problems step-by-step',
    domain: 'ai',
    status: 'achieved',
    yearAchieved: 2024,
    importance: 8,
    tier: 2,
    trl: 8,
    dependencies: ['ai-gpt4'],
    keyPlayers: ['OpenAI', 'Anthropic', 'Google DeepMind'],
    milestones: [
      {
        date: '2022-01-28',
        title: 'Chain-of-Thought Prompting',
        description: 'Wei et al. show step-by-step reasoning improves LLM performance',
        significance: 'breakthrough',
        source: 'https://arxiv.org/abs/2201.11903',
      },
      {
        date: '2024-09-12',
        title: 'o1 Preview Release',
        description: 'OpenAI releases reasoning-focused model',
        significance: 'major',
        source: 'https://openai.com/index/introducing-openai-o1-preview',
      },
    ],
  },
  {
    id: 'ai-agents',
    name: 'AI Agents',
    description: 'Autonomous systems that can use tools and complete tasks',
    domain: 'ai',
    status: 'in_progress',
    importance: 9,
    tier: 2,
    trl: 6,
    dependencies: ['ai-reasoning'],
    keyPlayers: ['Anthropic', 'OpenAI', 'Cognition'],
    progress: 60,
    progressMetric: 'SWE-Bench Score',
    progressValue: '49%',
    milestones: [
      {
        date: '2024-03-12',
        title: 'Devin Announcement',
        description: 'Cognition announces first AI software engineer',
        significance: 'major',
        source: 'https://cognition.ai/introducing-devin',
      },
      {
        date: '2024-10-22',
        title: 'Claude Computer Use',
        description: 'Anthropic releases computer-using agent capabilities',
        significance: 'major',
        source: 'https://www.theverge.com/2024/10/22/24276822/anthropic-claude-3-5-sonnet-computer-use-ai',
      },
    ],
  },
  {
    id: 'ai-multimodal',
    name: 'Multimodal Understanding',
    description: 'Models that seamlessly process text, images, audio, video',
    domain: 'ai',
    status: 'in_progress',
    importance: 8,
    tier: 2,
    trl: 7,
    dependencies: ['ai-gpt4'],
    keyPlayers: ['Google DeepMind', 'OpenAI', 'Meta'],
    progress: 75,
    milestones: [
      {
        date: '2023-09-25',
        title: 'GPT-4V Release',
        description: 'OpenAI adds vision to GPT-4 for image understanding',
        significance: 'major',
        source: 'https://openai.com/research/gpt-4v-system-card',
      },
      {
        date: '2024-02-15',
        title: 'Gemini 1.5',
        description: 'Google ships 1M-token context with native multimodal',
        significance: 'major',
        source: 'https://blog.google/technology/ai/google-gemini-next-generation-model-february-2024',
      },
    ],
  },

  // === GENERATION (Tier 2) ===
  {
    id: 'ai-image-gen',
    name: 'Image Generation',
    description: 'Photorealistic image synthesis from text descriptions',
    domain: 'ai',
    status: 'achieved',
    yearAchieved: 2022,
    importance: 7,
    tier: 2,
    trl: 8,
    dependencies: ['ai-neural-networks'],
    keyPlayers: ['Midjourney', 'Stability AI', 'OpenAI'],
    milestones: [
      {
        date: '2022-04-06',
        title: 'DALL-E 2',
        description: 'OpenAI releases high-fidelity text-to-image model',
        significance: 'breakthrough',
        source: 'https://openai.com/blog/dall-e-2-extending-creativity/',
      },
      {
        date: '2022-08-22',
        title: 'Stable Diffusion',
        description: 'Stability AI open-sources latent diffusion model',
        significance: 'breakthrough',
        source: 'https://stability.ai/news/stable-diffusion-public-release',
      },
      {
        date: '2022-07-12',
        title: 'Midjourney v3',
        description: 'Midjourney achieves photorealistic and artistic quality',
        significance: 'major',
        source: 'https://docs.midjourney.com/docs/model-versions',
      },
    ],
  },
  {
    id: 'ai-video-gen',
    name: 'Video Generation',
    description: 'Generating coherent video from text prompts',
    domain: 'ai',
    status: 'in_progress',
    importance: 8,
    tier: 2,
    trl: 6,
    dependencies: ['ai-image-gen'],
    keyPlayers: ['OpenAI', 'Runway', 'Google'],
    progress: 70,
    milestones: [
      {
        date: '2024-02-15',
        title: 'Sora Preview',
        description: 'OpenAI demonstrates minute-long coherent video generation',
        significance: 'breakthrough',
      },
    ],
  },

  // === FRONTIER (Tier 3) ===
  {
    id: 'ai-world-models',
    name: 'World Models',
    description: 'AI systems with internal models of physical reality',
    domain: 'ai',
    status: 'in_progress',
    importance: 9,
    tier: 3,
    trl: 4,
    dependencies: ['ai-multimodal', 'ai-reasoning'],
    keyPlayers: ['Google DeepMind', 'Meta FAIR'],
    progress: 30,
    milestones: [
      {
        date: '2018-03-27',
        title: 'World Models (Ha & Schmidhuber)',
        description: 'Neural network learns compressed spatial and temporal representation',
        significance: 'major',
        source: 'https://arxiv.org/abs/1803.10122',
      },
      {
        date: '2024-06-18',
        title: 'VEO 2 & Cat 2',
        description: 'Google DeepMind video and multi-step reasoning as world models',
        significance: 'major',
        source: 'https://deepmind.google/technologies/veo/',
      },
    ],
  },
  {
    id: 'ai-long-horizon',
    name: 'Long-Horizon Planning',
    description: 'AI that can plan and execute over extended timeframes',
    domain: 'ai',
    status: 'in_progress',
    importance: 8,
    tier: 3,
    trl: 4,
    dependencies: ['ai-agents', 'ai-world-models'],
    progress: 25,
    milestones: [
      {
        date: '2023-07-11',
        title: 'Voyager (PaLM-E)',
        description: 'Google embodied agent with long-horizon planning in real world',
        significance: 'major',
        source: 'https://arxiv.org/abs/2307.15880',
      },
      {
        date: '2024-05-14',
        title: 'Google DeepMind Astra',
        description: 'Multimodal agent with long-context planning and tool use',
        significance: 'major',
        source: 'https://deepmind.google/technologies/project-astra/',
      },
    ],
  },

  // === AGI THRESHOLD (Tier 4) ===
  {
    id: 'ai-agi-narrow',
    name: 'Expert-Level AGI',
    description: 'AI matching human experts across most cognitive tasks',
    domain: 'ai',
    status: 'in_progress',
    importance: 10,
    tier: 4,
    trl: 3,
    dependencies: ['ai-long-horizon', 'ai-world-models'],
    keyPlayers: ['OpenAI', 'Anthropic', 'Google DeepMind'],
    progress: 40,
    yearEstimated: 2027,
    milestones: [
      {
        date: '2024-09-12',
        title: 'o1 Class Models',
        description: 'OpenAI ships reasoning models approaching expert-level on benchmarks',
        significance: 'major',
        source: 'https://openai.com/index/introducing-openai-o1-preview',
      },
      {
        date: '2024-12-05',
        title: 'o1 and o1-mini Full Release',
        description: 'Production o1 with vision; o1-mini for cost-efficient reasoning',
        significance: 'major',
        source: 'https://openai.com/index/o1-and-new-tools-for-developers/',
      },
    ],
  },
  {
    id: 'ai-agi',
    name: 'Artificial General Intelligence',
    description: 'AI with human-level performance across all cognitive domains',
    domain: 'ai',
    status: 'theoretical',
    importance: 10,
    tier: 4,
    trl: 2,
    dependencies: ['ai-agi-narrow'],
    yearEstimated: 2030,
  },

  // === BEYOND AGI (Tier 5) ===
  {
    id: 'ai-recursive-improvement',
    name: 'Recursive Self-Improvement',
    description: 'AI systems capable of improving their own capabilities',
    domain: 'ai',
    status: 'theoretical',
    importance: 10,
    tier: 5,
    trl: 2,
    dependencies: ['ai-agi'],
    yearEstimated: 2032,
    milestones: [
      {
        date: '2022-12-15',
        title: 'Constitutional AI',
        description: 'Anthropic trains models using AI-generated feedback (RLAIF)',
        significance: 'major',
        source: 'https://arxiv.org/abs/2212.08073',
      },
      {
        date: '2022-12-20',
        title: 'Self-Instruct',
        description: 'Wang et al. use model-generated instruction data for finetuning',
        significance: 'major',
        source: 'https://arxiv.org/abs/2212.10560',
      },
    ],
  },
  {
    id: 'ai-asi',
    name: 'Artificial Superintelligence',
    description: 'Intelligence far surpassing all human cognitive capabilities',
    domain: 'ai',
    status: 'speculative',
    importance: 10,
    tier: 5,
    trl: 1,
    dependencies: ['ai-recursive-improvement'],
    milestones: [
      {
        date: '2014-01-01',
        title: 'Superintelligence (Bostrom)',
        description: 'Nick Bostrom’s book frames ASI and existential risk',
        significance: 'major',
        source: 'https://www.nickbostrom.com/superintelligence.html',
      },
      {
        date: '2023-05-30',
        title: 'Statement on AI Risk',
        description: 'Industry and researchers sign statement on mitigating extinction risk from AI',
        significance: 'major',
        source: 'https://safe.ai/statement-on-ai-risk',
      },
    ],
  },
];

export default AI_NODES;
